import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { IndividualDto } from "@models/individuals/individual-dto";
import { IndividualRecordShort } from "@models/individuals/individual-record-short";
import { Relationship } from "@models/relationships/relationship";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import {
  CommonModalWrapperComponent
} from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { AgAddressesComponent } from "@shared/components/grid/ag-cells/common/ag-addresses/ag-addresses.component";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import {
  AgIndividualNameComponent
} from "@shared/components/grid/ag-cells/common/ag-individual-name/ag-individual-name.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { IndividualsApiService } from "@shared/services/individuals-api.service";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { catchError, EMPTY, finalize, tap } from 'rxjs';

@Component({
  selector: 'app-select-individuals-modal',
  standalone: true,
  imports: [
    CommonModalWrapperComponent,
    LoaderComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'select-individuals-modal.component.html',
  styleUrl: 'select-individuals-modal.component.scss',
})
export class SelectIndividualsModalComponent implements OnInit {
  private individualApiService = inject(IndividualsApiService);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);

  @Input() individual!: IndividualDto;
  @Input() relationshipsIds: string[] = [];

  isLoading = signal<boolean>(false);
  individuals: IndividualRecordShort[] = [];
  selectedIndividualRecords: IndividualRecordShort[] = [];

  ngOnInit(): void {
    this.loadIndividuals();
  }

  loadIndividuals(): void {
    this.isLoading.set(true);
    this.individualApiService.getSplitList(this.individual.individualId).pipe(
      tap((individuals) => {
        if(!individuals.length) {
          this.toastr.warning("No individuals found to split.", 'Warning');
          this.activeModal.dismiss();
        }

        this.individuals = individuals;
        this.isLoading.set(false);
      }),
      finalize(() => this.isLoading.set(false)),
      catchError((error) => {
        console.warn('[SelectIndividualsModalComponent.loadIndividuals] error: ', error);
        this.isLoading.set(false);
        return EMPTY;
      }
    )).subscribe();
  }

  onSelectIndividual(individualRecords: IndividualRecordShort[]): void {
    this.selectedIndividualRecords = individualRecords;
  }

  submit(): void {
    if (!this.selectedIndividualRecords.length) {
      this.toastr.warning("Select individual to assign.", 'Warning');
      return;
    }

    this.isLoading.set(true);
    const individualIdSplitTo = this.selectedIndividualRecords[0].individualId;

    this.individualApiService.split(this.individual.individualId, this.relationshipsIds, individualIdSplitTo).pipe(
      tap(() => {
        this.toastr.success("Individuals split successfully.", 'Success');
        this.isLoading.set(false);
      }),
      finalize(() => this.isLoading.set(false)),
      catchError(() => {
        this.toastr.error("Error while splitting individuals");
        this.isLoading.set(false);
        return EMPTY;
      }))
      .subscribe({
        next: () => this.activeModal.close(),
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  colDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'fullName',
      minWidth: 150,
      flex: 1,
      sortable: false,
      editable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: AgIndividualNameComponent,
      checkboxSelection: true,
    },
    {
      headerName: 'Date of Birth',
      field: 'dob',
      minWidth: 130,
      width: 130,
      sortable: false,
      suppressHeaderMenuButton: true,
      valueGetter: (relationship: ValueGetterParams<Relationship>) => relationship.data?.individualDataOverride?.dob,
      cellRenderer: AgDateCellComponent
    },
    {
      headerName: 'Address',
      field: 'address',
      minWidth: 90,
      width: 90,
      flex: 2,
      sortable: false,
      suppressHeaderMenuButton: true,
      valueGetter: (params: ValueGetterParams<IndividualRecordShort>) => [params.data?.address],
      cellRenderer: AgAddressesComponent,
    },
    {
      headerName: 'Director ID',
      field: 'din',
      width: 150,
      minWidth: 150,
      sortable: false,
      suppressHeaderMenuButton: true,
    },
  ];
}
